import { ArrowRight } from 'lucide-react';
import { lazy } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { PageHeader, SectionTitle } from '@/components/ui';
import { TOOL_GROUPS, TOOLS, type ToolDefinition } from './registry';

const PedsTool = lazy(() => import('./pages/PedsTool'));
const RsiTool = lazy(() => import('./pages/RsiTool'));
const DosingTool = lazy(() => import('./pages/DosingTool'));
const InfusionTool = lazy(() => import('./pages/InfusionTool'));
const StrokeTool = lazy(() => import('./pages/StrokeTool'));
const BurnsTool = lazy(() => import('./pages/BurnsTool'));
const ShockIndexTool = lazy(() => import('./pages/VitalsTools').then((m) => ({ default: m.ShockIndexTool })));
const MapTool = lazy(() => import('./pages/VitalsTools').then((m) => ({ default: m.MapTool })));
const QsofaTool = lazy(() => import('./pages/VitalsTools').then((m) => ({ default: m.QsofaTool })));
const GcsTool = lazy(() => import('./pages/ScoreTools').then((m) => ({ default: m.GcsTool })));
const ApgarTool = lazy(() => import('./pages/ScoreTools').then((m) => ({ default: m.ApgarTool })));
const OxygenTool = lazy(() => import('./pages/UtilityTools').then((m) => ({ default: m.OxygenTool })));
const ConversionsTool = lazy(() => import('./pages/UtilityTools').then((m) => ({ default: m.ConversionsTool })));

const COMPONENTS: Record<string, React.ComponentType> = {
  peds: PedsTool,
  rsi: RsiTool,
  dosing: DosingTool,
  infusion: InfusionTool,
  stroke: StrokeTool,
  burns: BurnsTool,
  'shock-index': ShockIndexTool,
  map: MapTool,
  qsofa: QsofaTool,
  gcs: GcsTool,
  apgar: ApgarTool,
  oxygen: OxygenTool,
  conversions: ConversionsTool,
};

export function ToolRoute() {
  const { toolId = '' } = useParams();
  const Component = COMPONENTS[toolId];
  if (!Component) return <Navigate to="/tools" replace />;
  return <Component />;
}

/** Two-column index of tools, ruled like a reference manual's contents page. */
export function ToolIndex({ tools }: { tools: ToolDefinition[] }) {
  return (
    <div className="grid border-t border-stone-200 sm:grid-cols-2 sm:gap-x-10 dark:border-stone-800">
      {tools.map((t) => (
        <Link key={t.id} to={`/tools/${t.id}`} className="group flex items-start gap-3.5 border-b border-stone-200 py-3.5 dark:border-stone-800">
          <t.icon className="mt-0.5 size-4 shrink-0 text-stone-400 transition-colors group-hover:text-stone-900 dark:text-stone-500 dark:group-hover:text-white" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium leading-snug">{t.title}</span>
            <span className="mt-0.5 block text-sm leading-snug text-stone-500 dark:text-stone-400">{t.description}</span>
          </span>
          <ArrowRight className="mt-1 size-4 shrink-0 text-stone-300 transition group-hover:translate-x-0.5 group-hover:text-stone-900 dark:text-stone-600 dark:group-hover:text-white" />
        </Link>
      ))}
    </div>
  );
}

export function ToolsPage() {
  return (
    <div>
      <PageHeader title="Clinical tools" subtitle="Point-of-care calculators. Every tool works offline." />
      <div className="space-y-9">
        {TOOL_GROUPS.map((group) => (
          <section key={group}>
            <SectionTitle>{group}</SectionTitle>
            <ToolIndex tools={TOOLS.filter((t) => t.group === group)} />
          </section>
        ))}
      </div>
    </div>
  );
}
